import React, { useState, useRef, useEffect, useCallback } from 'react';
import ExcelJS from 'exceljs';
import { toast } from 'react-toastify';
import { chinaOrderImportService } from '../../../../service/admin/chinaOrderImportService';
import { EditModal, DeleteModal, fetchAllRowsPaged } from '../ActionModals';
import './style.css';
import '../style.css';

// SVG Icons
const UploadIcon = () => (
  <svg className="upload-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
    <polyline points="17 8 12 3 7 8"></polyline>
    <line x1="12" y1="3" x2="12" y2="15"></line>
  </svg>
);

const CheckIcon = () => (
  <svg className="check-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
    <polyline points="22 4 12 14.01 9 11.01"></polyline>
  </svg>
);

const TABLE_COLUMNS = [
  { key: 'chinaSNo',        label: 'China S.No.',       group: 'order' },
  { key: 'indiaSNo',        label: 'India S. No.',      group: 'order' },
  { key: 'entryDate',       label: 'Entry Date',        group: 'order' },
  { key: 'orderId',         label: 'Order ID',          group: 'order' },
  { key: 'qty',             label: 'Qty',               group: 'order' },
  { key: 'prodName',        label: 'Prod Name',         group: 'order',    wrap: 'prodName' },
  { key: 'orderComments',   label: 'Order Comments',    group: 'order',    wrap: 'comments' },
  { key: 'url',             label: 'URL',               group: 'purchase' },
  { key: 'purchaseQty',     label: 'Purchase Qty.',     group: 'purchase' },
  { key: 'unit',            label: 'Unit',              group: 'purchase' },
  { key: 'variant',         label: 'Variant',           group: 'purchase', wrap: 'variant' },
  { key: 'unitPrice',       label: 'Unit Price',        group: 'purchase' },
  { key: 'qtyPrice',        label: 'Qty Price',         group: 'purchase' },
  { key: 'localShipping',   label: 'Local Shipping',    group: 'purchase' },
  { key: 'totalPrice',      label: 'Total Price',       group: 'purchase' },
  { key: 'maskId',          label: 'Mask ID',           group: 'shipping' },
  { key: 'shippingDate',    label: 'Shipping Date',     group: 'shipping' },
  { key: 'shippingCompany', label: 'Shipping Comments', group: 'shipping', wrap: 'shipping' },
  { key: 'qtyShipped',      label: 'Qty Shipped',       group: 'shipping' },
  { key: 'status',          label: 'Status',            group: 'shipping' },
  { key: 'hsn',             label: 'HSN',               group: 'shipping' },
  { key: 'estimatedDOA',    label: 'Estimated DOA',     group: 'manual' },
];

const ORDER_COLS    = TABLE_COLUMNS.filter(c => c.group === 'order');
const PURCHASE_COLS = TABLE_COLUMNS.filter(c => c.group === 'purchase');
const SHIPPING_COLS = TABLE_COLUMNS.filter(c => c.group === 'shipping');
const MANUAL_COLS   = TABLE_COLUMNS.filter(c => c.group === 'manual');

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const INDIA_SNO_FILTER_OPTIONS = [
  { value: '', label: 'All India S.No.' },
  { value: 'bshopy', label: 'F - bshopy' },
  { value: 'myntra', label: 'MT - myntra' },
  { value: 'dropshipper', label: 'BS - dropshipper' },
  { value: 'amazon', label: 'Amazon' },
  { value: 'custom', label: 'Custom prefix' },
];
const SOURCE_FILTER_OPTIONS = [
  { value: '', label: 'All Source' },
  { value: 'Yeky', label: 'Yeky' },
  { value: 'Sunny', label: 'Sunny' },
  { value: 'custom', label: 'Custom' },
];
const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'ordered', label: 'ordered' },
  { value: 'cancel', label: 'cancel' },
  { value: 'ordermade', label: 'ordermade' },
  { value: 'Shipped', label: 'Shipped' },
  { value: 'return back', label: 'return back' },
  { value: '__NULL__', label: 'null' },
  { value: 'custom', label: 'Custom' },
];
const BULK_BATCH_MIN_ROWS = 100;
const BULK_CHUNK_COUNT = 10;
const TARGET_ROWS_PER_BATCH = 2500;
const MAX_DYNAMIC_BATCHES = 40;
const IMPORT_BATCH_CONCURRENCY = 3;
const TERMINAL_SHEET_SYNC = new Set(['completed', 'failed', 'partial_failed', 'cancelled']);
const RUNNING_SHEET_SYNC = new Set(['pending', 'processing']);
/** One list API call per poll — avoids N parallel get-by-id queries under load. */
const SHEET_SYNC_POLL_LIST_LIMIT = 50;
/** Healthy polling interval (was 1.5s × N jobs). */
const SHEET_SYNC_POLL_BASE_MS = 4000;
const SHEET_SYNC_POLL_MAX_BACKOFF_MS = 32000;
// When true, upload the full workbook in one request (faster prep, but progress stays ~5% until the HTTP call finishes).
// When false, the file is split into batches so the progress bar can advance between uploads; row offsets are sent so error rows match the original sheet.
const ENABLE_DIRECT_IMPORT_UPLOAD = false;

const splitIntoNBatches = (rows, n) => {
  if (!rows.length) return [];
  const batches = Array.from({ length: n }, () => []);
  const base = Math.floor(rows.length / n);
  let rem = rows.length % n;
  let idx = 0;
  for (let b = 0; b < n; b += 1) {
    const size = base + (rem > 0 ? 1 : 0);
    if (rem > 0) rem -= 1;
    batches[b] = rows.slice(idx, idx + size);
    idx += size;
  }
  return batches.filter((batch) => batch.length > 0);
};

const normalizeKey = (value) => (value === null || value === undefined ? '' : String(value).trim());

/** Match backend chinaOrderImportController: rich-text / object Excel cells → display text */
const headerCellToText = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') {
    if (value instanceof Date) return String(value);
    if (value.hyperlink) return String(value.hyperlink);
    if (value.richText && Array.isArray(value.richText)) {
      return value.richText.map((rt) => rt.text).join('');
    }
    if (value.text) return String(value.text);
  }
  return String(value);
};

const canonicalSheetHeader = (value) =>
  headerCellToText(value)
    .replace(/\u00A0/g, ' ')
    .replace(/\u200B/g, '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/\.+$/g, '')
    .replace(/[^a-z0-9]/g, '');

const resolvePostageHeaderRowNum = (postageSheet) => {
  const indicators = ['invoiceno', 'invno', 'shipdate', 'chinalocaltrackingid'];
  for (let r = 1; r <= 5; r += 1) {
    const row = postageSheet.getRow(r);
    let found = false;
    row.eachCell((cell) => {
      if (!found) {
        const ck = canonicalSheetHeader(cell.value);
        if (indicators.includes(ck)) found = true;
      }
    });
    if (found) return r;
  }
  return 1;
};

const buildRowCellTexts = (sheet, rowNum) => {
  const row = sheet.getRow(rowNum);
  const arr = [];
  row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    arr[colNumber - 1] = headerCellToText(cell.value);
  });
  return arr;
};

const buildFixedRowValues = (row, colCount) =>
  Array.from({ length: colCount }, (_, i) => row.getCell(i + 1).value ?? null);

/** Highlight known sheet column phrases inside error reason text */
const IMPORT_REASON_FIELD_MARKERS = [
  'Invoice No',
  'China Local Tracking ID',
  'China S.No.',
  'India S. No.',
];

const renderImportReason = (reason) => {
  const text = reason || 'Unknown error';
  for (let i = 0; i < IMPORT_REASON_FIELD_MARKERS.length; i += 1) {
    const phrase = IMPORT_REASON_FIELD_MARKERS[i];
    const idx = text.indexOf(phrase);
    if (idx >= 0) {
      return (
        <>
          {idx > 0 ? text.slice(0, idx) : null}
          <span className="od-sheet-field">{phrase}</span>
          {text.slice(idx + phrase.length)}
        </>
      );
    }
  }
  return text;
};

/** Batch/source hints in import API error strings */
const renderImportApiError = (msg) => {
  const m = /^(.+?)\s*\(batch\s*(\d+)\)\s*:\s*(.*)$/i.exec(String(msg || ''));
  if (m) {
    return (
      <>
        <span className="od-sheet-meta">{m[1].trim()}</span>
        {' '}
        <span className="od-sheet-value">batch {m[2]}</span>
        <span className="od-failed-sep">: </span>
        <span className="od-failed-reason">{m[3]}</span>
      </>
    );
  }
  return msg;
};

const getDynamicChunkCount = (rowCount) => {
  if (rowCount < BULK_BATCH_MIN_ROWS) return 1;
  const byTargetSize = Math.ceil(rowCount / TARGET_ROWS_PER_BATCH);
  return Math.min(MAX_DYNAMIC_BATCHES, Math.max(BULK_CHUNK_COUNT, byTargetSize));
};

/** Merge sheet sync jobs from one import API response (single job or jobs array). */
function mergeSheetSyncJobsFromResponse(existing, sheetSync) {
  const incoming = [];
  if (sheetSync?.jobs?.length) incoming.push(...sheetSync.jobs);
  else if (sheetSync?.job?.id) incoming.push(sheetSync.job);
  const map = new Map((existing || []).map((j) => [j.id, j]));
  incoming.forEach((j) => {
    if (j?.id) map.set(j.id, { ...map.get(j.id), ...j });
  });
  return [...map.values()];
}

/** Merge `listSheetSyncJobs` rows into tracked jobs for the ids we are polling (must not rely on setState updater side effects). */
function mergeSheetSyncPollSlice(prevJobs, listRows, liveIds) {
  const prevById = new Map((prevJobs || []).map((j) => [String(j.id), j]));
  (listRows || []).forEach((fresh) => {
    if (fresh?.id != null) prevById.set(String(fresh.id), fresh);
  });
  return liveIds.map((id) => prevById.get(String(id))).filter(Boolean);
}

/** One status line when sheet sync job(s) are shown in the banner. */
function sheetSyncAggregateStatusWord(jobs) {
  if (!jobs?.length) return "";
  if (jobs.some((j) => !TERMINAL_SHEET_SYNC.has(j.status))) {
    return jobs.some((j) => j.status === "pending") ? "Pending" : "Processing";
  }
  if (jobs.every((j) => j.status === "cancelled")) return "Cancelled";
  if (jobs.some((j) => j.status === "cancelled")) return "Stopped";
  if (jobs.every((j) => j.status === "completed")) return "Completed";
  if (jobs.some((j) => j.status === "partial_failed")) return "Partial failed";
  if (jobs.every((j) => j.status === "failed")) return "Failed";
  if (jobs.some((j) => j.status === "failed")) return "Partial failed";
  return "Completed";
}

export default function OrderDetails() {
  // ---- Upload state ----
  const [yekyFileName, setYekyFileName]   = useState(null);
  const [sunnyFileName, setSunnyFileName] = useState(null);
  const [isImporting, setIsImporting]     = useState(false);
  const yekyFileRef  = useRef(null);
  const sunnyFileRef = useRef(null);
  const yekyDragDepth = useRef(0);
  const sunnyDragDepth = useRef(0);
  const [yekyDragOver, setYekyDragOver] = useState(false);
  const [sunnyDragOver, setSunnyDragOver] = useState(false);

  /** Latest Google Sheet background sync job (from import or API). */
  const [sheetSyncJob, setSheetSyncJob] = useState(null);
  /** One or more jobs (e.g. import chunks); aggregated in the top banner with a progress bar. */
  const [sheetSyncTrackedJobs, setSheetSyncTrackedJobs] = useState([]);
  /** Mirrors `sheetSyncTrackedJobs` for async poll loop (avoid trusting setState updater side effects). */
  const sheetSyncTrackedJobsRef = useRef([]);
  /** Bump when `sheetSyncPollIdsRef` should be polled (new import finished with sheet jobs). */
  const [sheetSyncPollVersion, setSheetSyncPollVersion] = useState(0);
  const pendingSheetSyncJobsRef = useRef([]);
  const sheetSyncPollIdsRef = useRef([]);

  useEffect(() => {
    sheetSyncTrackedJobsRef.current = sheetSyncTrackedJobs;
  }, [sheetSyncTrackedJobs]);

  // ---- Table / pagination state ----
  const [records, setRecords]         = useState([]);
  const [isLoading, setIsLoading]     = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch]           = useState('');
  const [pagination, setPagination]   = useState({ page: 1, limit: 20, total: 0, totalPages: 1 });

  // ---- Date filter state (IST) ----
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate]     = useState('');
  const [indiaSNoType, setIndiaSNoType] = useState('');
  const [indiaSNoCustomPrefix, setIndiaSNoCustomPrefix] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSourceCustom, setFilterSourceCustom] = useState('');
  const [filterStatusCustom, setFilterStatusCustom] = useState('');
  const hasCustomFilter = indiaSNoType === 'custom' || filterSource === 'custom' || filterStatus === 'custom';
  const hasAnyActiveFilter = Boolean(
    startDate ||
    endDate ||
    indiaSNoType ||
    indiaSNoCustomPrefix ||
    filterSource ||
    filterSourceCustom ||
    filterStatus ||
    filterStatusCustom
  );

  const [isExporting, setIsExporting] = useState(false);
  const [showExportProgressModal, setShowExportProgressModal] = useState(false);
  const [exportProgressPercent, setExportProgressPercent] = useState(0);
  const [exportProgressLabel, setExportProgressLabel] = useState('');
  const [showFailedModal, setShowFailedModal] = useState(false);
  const [failedSummary, setFailedSummary] = useState([]);
  const [showImportProgressModal, setShowImportProgressModal] = useState(false);
  const [importProgressPercent, setImportProgressPercent] = useState(0);
  const [importProgressLabel, setImportProgressLabel] = useState("Preparing...");

  // ---- Edit / Delete modal state ----
  const [editRow, setEditRow]         = useState(null);
  const [deleteRow, setDeleteRow]     = useState(null);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [showFullSheetSyncConfirm, setShowFullSheetSyncConfirm] = useState(false);
  const [isFullSheetSyncing, setIsFullSheetSyncing] = useState(false);
  const [isCancellingSheetSync, setIsCancellingSheetSync] = useState(false);

  // ---- Fetch from DB ----
  const fetchRecords = useCallback(async (
    page = 1,
    searchVal = '',
    limit = pagination.limit,
    sd = startDate,
    ed = endDate,
    indiaType = indiaSNoType,
    indiaPrefix = indiaSNoCustomPrefix,
    src = filterSource,
    stat = filterStatus,
    srcCustom = filterSourceCustom,
    statCustom = filterStatusCustom
  ) => {
    setIsLoading(true);
    try {
      const resolvedSource = src === 'custom' ? String(srcCustom || '').trim() : src;
      const resolvedStatus = stat === 'custom' ? String(statCustom || '').trim() : stat;
      const res = await chinaOrderImportService.getOrderDetails({
        page,
        limit,
        search: searchVal,
        startDate: sd,
        endDate: ed,
        indiaSNoType: indiaType,
        indiaSNoCustomPrefix: indiaType === 'custom' ? indiaPrefix : '',
        source: resolvedSource,
        status: resolvedStatus,
      });
      if (res?.status?.code === 200) {
        setRecords(res.data.records);
        setPagination(prev => ({ ...prev, ...res.data.pagination }));
      } else {
        toast.error(res?.error?.message || 'Failed to load records.');
      }
    } catch {
      toast.error('Failed to load Order Details records.');
    } finally {
      setIsLoading(false);
    }
  }, [pagination.limit, startDate, endDate, indiaSNoType, indiaSNoCustomPrefix, filterSource, filterStatus, filterSourceCustom, filterStatusCustom]);

  useEffect(() => { fetchRecords(1, '', 20); }, []);

  /** Re-attach sheet sync banner after navigating away: restore pending/processing jobs from the API. */
  useEffect(() => {
    if (isImporting) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await chinaOrderImportService.listSheetSyncJobs({ limit: SHEET_SYNC_POLL_LIST_LIMIT });
        if (cancelled || res?.status?.code !== 200) return;
        const activeJobs = (res.data?.jobs || []).filter(
          (j) =>
            j.syncType === 'china_order_details' &&
            RUNNING_SHEET_SYNC.has(j.status)
        );
        if (!activeJobs.length) return;
        if (sheetSyncPollIdsRef.current.length > 0) return;

        sheetSyncPollIdsRef.current = activeJobs.map((j) => j.id).filter(Boolean);
        sheetSyncTrackedJobsRef.current = activeJobs;
        setSheetSyncTrackedJobs(activeJobs);
        setSheetSyncJob(activeJobs[0]);
        setSheetSyncPollVersion((v) => v + 1);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isImporting]);

  /** Poll tracked sheet jobs: one list endpoint per tick + fixed interval + backoff on errors. */
  useEffect(() => {
    if (!sheetSyncPollVersion) return undefined;
    const idsAtStart = sheetSyncPollIdsRef.current.filter(Boolean);
    if (!idsAtStart.length) return undefined;

    let cancelled = false;
    let timeoutId;
    let errorStreak = 0;

    const scheduleNext = (delayMs) => {
      clearTimeout(timeoutId);
      if (cancelled || !sheetSyncPollIdsRef.current.length) return;
      timeoutId = setTimeout(loop, delayMs);
    };

    const loop = async () => {
      const liveIds = sheetSyncPollIdsRef.current.filter(Boolean);
      if (!liveIds.length || cancelled) return;

      const idSet = new Set(liveIds);
      let ok = false;
      let rows = [];

      try {
        const res = await chinaOrderImportService.listSheetSyncJobs({
          limit: SHEET_SYNC_POLL_LIST_LIMIT,
        });
        ok = res?.status?.code === 200;
        if (ok) {
          rows = (res.data?.jobs || []).filter((j) => idSet.has(j.id));
        }
      } catch {
        ok = false;
      }

      if (cancelled) return;

      if (ok) {
        errorStreak = 0;
        const mergedNext = mergeSheetSyncPollSlice(sheetSyncTrackedJobsRef.current, rows, liveIds);
        sheetSyncTrackedJobsRef.current = mergedNext;
        setSheetSyncTrackedJobs(mergedNext);

        if (mergedNext.length && mergedNext[0]) {
          setSheetSyncJob(mergedNext[0]);
        }

        const mergedById = new Map(mergedNext.map((j) => [String(j.id), j]));
        const allTerminal =
          liveIds.length > 0 &&
          liveIds.every((id) => {
            const j = mergedById.get(String(id));
            return j && TERMINAL_SHEET_SYNC.has(j.status);
          });
        if (allTerminal) {
          sheetSyncPollIdsRef.current = [];
          return;
        }

        scheduleNext(SHEET_SYNC_POLL_BASE_MS);
        return;
      }

      errorStreak += 1;
      const backoff = Math.min(
        SHEET_SYNC_POLL_MAX_BACKOFF_MS,
        SHEET_SYNC_POLL_BASE_MS * 2 ** Math.min(errorStreak, 4)
      );
      scheduleNext(backoff);
    };

    loop();
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [sheetSyncPollVersion]);

  /** Single-job refresh (edit/delete path): same light list API, no parallel get-by-id. */
  useEffect(() => {
    if (!sheetSyncJob?.id) return undefined;
    if (TERMINAL_SHEET_SYNC.has(sheetSyncJob.status)) return undefined;
    const jobId = sheetSyncJob.id;

    let cancelled = false;
    let timeoutId;
    let errorStreak = 0;

    const scheduleNext = (delayMs) => {
      clearTimeout(timeoutId);
      if (cancelled) return;
      timeoutId = setTimeout(loop, delayMs);
    };

    const loop = async () => {
      if (cancelled || sheetSyncPollIdsRef.current.length) return;

      let ok = false;
      try {
        const res = await chinaOrderImportService.listSheetSyncJobs({
          limit: SHEET_SYNC_POLL_LIST_LIMIT,
        });
        ok = res?.status?.code === 200;
        if (ok) {
          errorStreak = 0;
          const j = (res.data?.jobs || []).find((x) => x.id === jobId);
          if (j) setSheetSyncJob(j);
          if (j && TERMINAL_SHEET_SYNC.has(j.status)) return;
        }
      } catch {
        ok = false;
      }

      if (cancelled) return;

      if (ok) {
        scheduleNext(SHEET_SYNC_POLL_BASE_MS);
        return;
      }

      errorStreak += 1;
      const backoff = Math.min(
        SHEET_SYNC_POLL_MAX_BACKOFF_MS,
        SHEET_SYNC_POLL_BASE_MS * 2 ** Math.min(errorStreak, 4)
      );
      scheduleNext(backoff);
    };

    loop();
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [sheetSyncJob?.id, sheetSyncJob?.status]);

  const beginSheetSyncProgressFromJobs = useCallback((jobs) => {
    if (!Array.isArray(jobs) || jobs.length === 0) return;
    sheetSyncPollIdsRef.current = jobs.map((j) => j.id).filter(Boolean);
    sheetSyncTrackedJobsRef.current = jobs;
    setSheetSyncTrackedJobs(jobs);
    setSheetSyncJob(jobs[0]);
    setSheetSyncPollVersion((v) => v + 1);
  }, []);

  const handleFullSheetSyncConfirmed = async () => {
    setIsFullSheetSyncing(true);
    try {
      const res = await chinaOrderImportService.syncAllOrderDetailsToLiveSheet();
      if (!res || res.status?.code !== 200) {
        toast.error(res?.error?.message || 'Failed to queue full sheet sync.');
        return;
      }
      const merged = mergeSheetSyncJobsFromResponse([], res.data?.sheetSync);
      if (merged.length > 0) {
        beginSheetSyncProgressFromJobs(merged);
        toast.success(res.data?.message || 'Sheet sync queued.');
      } else {
        toast.info(res.data?.message || 'Nothing queued for sync.');
      }
      setShowFullSheetSyncConfirm(false);
    } catch (err) {
      toast.error(err?.message || err?.data?.error?.message || 'Failed to queue full sheet sync.');
    } finally {
      setIsFullSheetSyncing(false);
    }
  };

  const handleCancelSheetSync = async () => {
    const jobs =
      sheetSyncTrackedJobs.length > 0 ? sheetSyncTrackedJobs : sheetSyncJob ? [sheetSyncJob] : [];
    const ids = jobs
      .filter((j) => j.status === 'pending' || j.status === 'processing')
      .map((j) => j.id)
      .filter(Boolean);
    if (!ids.length) return;
    setIsCancellingSheetSync(true);
    try {
      const res = await chinaOrderImportService.cancelSheetSyncJobs(ids);
      if (!res || res.status?.code !== 200) {
        toast.error(res?.error?.message || 'Failed to cancel sync.');
        return;
      }
      sheetSyncPollIdsRef.current = [];
      setSheetSyncPollVersion((v) => v + 1);
      setSheetSyncTrackedJobs((prev) =>
        prev.length
          ? prev.map((j) =>
              ids.includes(j.id)
                ? { ...j, status: 'cancelled', lastError: 'Cancelled by admin' }
                : j
            )
          : prev
      );
      setSheetSyncJob((prev) => {
        if (!prev || !ids.includes(prev.id)) return prev;
        return { ...prev, status: 'cancelled', lastError: 'Cancelled by admin' };
      });
      toast.success(res.data?.message || 'Sync cancelled.');
    } catch (err) {
      toast.error(err?.message || 'Failed to cancel sync.');
    } finally {
      setIsCancellingSheetSync(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    fetchRecords(1, searchInput, pagination.limit, startDate, endDate, indiaSNoType, indiaSNoCustomPrefix, filterSource, filterStatus);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearch('');
    fetchRecords(1, '', pagination.limit, startDate, endDate, indiaSNoType, indiaSNoCustomPrefix, filterSource, filterStatus);
  };

  const handleLimitChange = (e) => {
    const newLimit = parseInt(e.target.value);
    setPagination(prev => ({ ...prev, limit: newLimit }));
    fetchRecords(1, search, newLimit, startDate, endDate, indiaSNoType, indiaSNoCustomPrefix, filterSource, filterStatus);
  };

  const handlePageChange = (newPage) =>
    fetchRecords(newPage, search, pagination.limit, startDate, endDate, indiaSNoType, indiaSNoCustomPrefix, filterSource, filterStatus);

  const handleDateFilter = () =>
    fetchRecords(1, search, pagination.limit, startDate, endDate, indiaSNoType, indiaSNoCustomPrefix, filterSource, filterStatus, filterSourceCustom, filterStatusCustom);

  const handleClearDates = () => {
    setStartDate('');
    setEndDate('');
    setIndiaSNoType('');
    setIndiaSNoCustomPrefix('');
    setFilterSource('');
    setFilterSourceCustom('');
    setFilterStatus('');
    setFilterStatusCustom('');
    fetchRecords(1, search, pagination.limit, '', '', '', '', '', '', '', '');
  };

  // ---- File upload handlers ----
  const handleYekyFileChange  = (e) => setYekyFileName(e.target.files[0]  ? e.target.files[0].name  : null);
  const handleSunnyFileChange = (e) => setSunnyFileName(e.target.files[0] ? e.target.files[0].name : null);

  const clearYekyFile = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (yekyFileRef.current) yekyFileRef.current.value = '';
    setYekyFileName(null);
  };

  const clearSunnyFile = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (sunnyFileRef.current) sunnyFileRef.current.value = '';
    setSunnyFileName(null);
  };

  const firstExcelFromFileList = (fileList) => {
    if (!fileList?.length) return null;
    for (let i = 0; i < fileList.length; i += 1) {
      const f = fileList[i];
      if (f && /\.(xlsx|xls)$/i.test(f.name)) return f;
    }
    return null;
  };

  const assignExcelToInput = (inputRef, file, setFileName) => {
    if (!file?.name) return false;
    if (!/\.(xlsx|xls)$/i.test(file.name)) {
      toast.warn('Please use an Excel file (.xlsx or .xls).');
      return false;
    }
    const input = inputRef.current;
    if (!input) return false;
    try {
      const dt = new DataTransfer();
      dt.items.add(file);
      input.files = dt.files;
    } catch {
      toast.error('Drag and drop failed in this browser. Click the zone to browse.');
      return false;
    }
    setFileName(file.name);
    return true;
  };

  const buildImportChunks = async (file) => {
    try {
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(await file.arrayBuffer());
      const orderSheet = workbook.getWorksheet("Order Details");
      const postageSheet = workbook.getWorksheet("Postage to IND");
      if (!orderSheet || !postageSheet) {
        return {
          chunkFiles: [file],
          importMeta: {
            orderDetails: { uniqueKeys: 0, duplicateRows: [] },
            postageToInd: { uniqueKeys: 0, duplicateRows: [] },
          },
          chunkBuilder: null,
          computeChunkRowMeta: null,
        };
      }

      const orderTitle = orderSheet.getRow(1)?.values?.slice(1) || [];
      const orderHeader = orderSheet.getRow(2)?.values?.slice(1) || [];
      const postageHeaderRowNum = resolvePostageHeaderRowNum(postageSheet);
      const postageHeader = buildRowCellTexts(postageSheet, postageHeaderRowNum);
      const orderKeyIndex = orderHeader.findIndex(
        (h) => canonicalSheetHeader(h) === 'chinasno' || normalizeKey(h) === 'China S.No.'
      );
      const postageKeyIndex = postageHeader.findIndex((h) => canonicalSheetHeader(h) === 'invoiceno');

      const orderColCount = Math.max(orderHeader.length, orderSheet.actualColumnCount || 0);
      const postageColCount = Math.max(postageHeader.length, postageSheet.actualColumnCount || 0);

      const orderRows = [];
      const orderExcelRows = [];
      const postageRows = [];
      const postageExcelRows = [];
      const orderKeyCounts = new Map();
      const postageKeyCounts = new Map();

      if (orderSheet) {
        orderSheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
          if (rowNumber > 2) {
            const rowValues = buildFixedRowValues(row, orderColCount);
            orderRows.push(rowValues);
            orderExcelRows.push(rowNumber);
            if (orderKeyIndex >= 0) {
              const key = normalizeKey(headerCellToText(rowValues[orderKeyIndex]));
              if (key) orderKeyCounts.set(key, (orderKeyCounts.get(key) || 0) + 1);
            }
          }
        });
      }
      if (postageSheet) {
        postageSheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
          if (rowNumber > postageHeaderRowNum) {
            const rowValues = buildFixedRowValues(row, postageColCount);
            postageRows.push(rowValues);
            postageExcelRows.push(rowNumber);
            if (postageKeyIndex >= 0) {
              const key = normalizeKey(headerCellToText(rowValues[postageKeyIndex]));
              if (key) postageKeyCounts.set(key, (postageKeyCounts.get(key) || 0) + 1);
            }
          }
        });
      }
      const importMeta = {
        orderDetails: {
          uniqueKeys: orderKeyCounts.size,
          duplicateRows: Array.from(orderKeyCounts.entries())
            .filter(([, count]) => count > 1)
            .map(([key, count]) => ({ key, count })),
        },
        postageToInd: {
          uniqueKeys: postageKeyCounts.size,
          duplicateRows: Array.from(postageKeyCounts.entries())
            .filter(([, count]) => count > 1)
            .map(([key, count]) => ({ key, count })),
        },
      };
      const maxRows = Math.max(orderRows.length, postageRows.length);
      if (maxRows < BULK_BATCH_MIN_ROWS) {
        return { chunkFiles: [file], importMeta, chunkBuilder: null, computeChunkRowMeta: null };
      }

      const indexes = Array.from({ length: maxRows }, (_, i) => i);
      const chunkCount = getDynamicChunkCount(maxRows);
      const indexBatches = splitIntoNBatches(indexes, chunkCount);
      const ORDER_CHUNK_FIRST_DATA_ROW = 3;
      const postageFirstDataRowInChunk = postageHeaderRowNum + 1;

      const computeChunkRowMeta = (batchIndex) => {
        const batch = indexBatches[batchIndex] || [];
        let firstOrderExcel = null;
        let firstPostageExcel = null;
        for (let b = 0; b < batch.length; b += 1) {
          const idx = batch[b];
          if (firstOrderExcel == null && orderRows[idx]) firstOrderExcel = orderExcelRows[idx];
          if (firstPostageExcel == null && postageRows[idx]) firstPostageExcel = postageExcelRows[idx];
          if (firstOrderExcel != null && firstPostageExcel != null) break;
        }
        return {
          orderErrorRowDelta: firstOrderExcel != null ? firstOrderExcel - ORDER_CHUNK_FIRST_DATA_ROW : 0,
          postageErrorRowDelta:
            firstPostageExcel != null ? firstPostageExcel - postageFirstDataRowInChunk : 0,
        };
      };

      const chunkBuilder = async (batchIndex) => {
        const wb = new ExcelJS.Workbook();
        const wsOrder = wb.addWorksheet("Order Details");
        const wsPostage = wb.addWorksheet("Postage to IND");
        wsOrder.addRow(orderTitle);
        wsOrder.addRow(orderHeader);
        for (let r = 1; r < postageHeaderRowNum; r += 1) {
          wsPostage.addRow(buildRowCellTexts(postageSheet, r));
        }
        wsPostage.addRow(postageHeader);
        const batch = indexBatches[batchIndex] || [];
        batch.forEach((idx) => {
          if (orderRows[idx]) wsOrder.addRow(orderRows[idx]);
          if (postageRows[idx]) wsPostage.addRow(postageRows[idx]);
        });
        const buf = await wb.xlsx.writeBuffer();
        return new File(
          [buf],
          `${file.name.replace(/\.xlsx?$/i, '')}_part_${batchIndex + 1}.xlsx`,
          { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
        );
      };

      return {
        chunkFiles: Array.from({ length: indexBatches.length }, (_, i) => i),
        importMeta,
        chunkBuilder,
        computeChunkRowMeta,
      };
    } catch {
      return {
        chunkFiles: [file],
        importMeta: {
          orderDetails: { uniqueKeys: 0, duplicateRows: [] },
          postageToInd: { uniqueKeys: 0, duplicateRows: [] },
        },
        chunkBuilder: null,
        computeChunkRowMeta: null,
      };
    }
  };

  const handleImportAll = async () => {
    const yekyFile  = yekyFileRef.current?.files[0];
    const sunnyFile = sunnyFileRef.current?.files[0];

    if (!yekyFile && !sunnyFile) {
      toast.warn('Please select at least one file (Yeky or Sunny) to import.');
      return;
    }

    setIsImporting(true);
    setShowImportProgressModal(true);
    setImportProgressPercent(5);
    setImportProgressLabel("Preparing import...");
    pendingSheetSyncJobsRef.current = [];
    sheetSyncPollIdsRef.current = [];
    sheetSyncTrackedJobsRef.current = [];
    setSheetSyncTrackedJobs([]);
    const uploads = [];
    if (yekyFile)  uploads.push({ file: yekyFile,  source: 'Yeky' });
    if (sunnyFile) uploads.push({ file: sunnyFile, source: 'Sunny' });

    const errors = [];
    const importResults = [];
    const mergeDuplicateRows = (existing, incoming) => {
      const mergedMap = new Map();
      [...(existing || []), ...(incoming || [])].forEach((d) => {
        const key = normalizeKey(d?.key);
        const count = Number(d?.count || 0);
        if (!key || !count) return;
        const prev = mergedMap.get(key) || 0;
        mergedMap.set(key, Math.max(prev, count));
      });
      return Array.from(mergedMap.entries()).map(([key, count]) => ({ key, count }));
    };

    const plans = [];
    for (let i = 0; i < uploads.length; i += 1) {
      const { file, source } = uploads[i];
      if (ENABLE_DIRECT_IMPORT_UPLOAD) {
        // Fast path: avoid expensive browser-side workbook split/rebuild for very large files.
        plans.push({
          source,
          chunkFiles: [file],
          chunkBuilder: null,
          computeChunkRowMeta: null,
          importMeta: {
            orderDetails: { uniqueKeys: 0, duplicateRows: [] },
            postageToInd: { uniqueKeys: 0, duplicateRows: [] },
          },
        });
      } else {
        const { chunkFiles, importMeta, chunkBuilder, computeChunkRowMeta } = await buildImportChunks(file);
        plans.push({ source, chunkFiles, importMeta, chunkBuilder, computeChunkRowMeta });
      }
    }

    const totalBatches = plans.reduce((sum, p) => sum + p.chunkFiles.length, 0) || 1;
    let doneBatches = 0;

    for (let p = 0; p < plans.length; p += 1) {
      const { source, chunkFiles, importMeta, chunkBuilder, computeChunkRowMeta } = plans[p];
      const merged = {
        orderDetails: { total: 0, created: 0, updated: 0, failedRows: [], duplicateRows: [] },
        postageToInd: { total: 0, created: 0, updated: 0, failedRows: [], duplicateRows: [] },
        importErrors: [],
      };

      let nextBatchIndex = 0;
      const runBatchWorker = async () => {
        while (true) {
          const currentIndex = nextBatchIndex;
          nextBatchIndex += 1;
          if (currentIndex >= chunkFiles.length) break;
          let progressPulse = null;
          try {
            setImportProgressLabel(`${source}: batch ${currentIndex + 1}/${chunkFiles.length} processing...`);
            setImportProgressPercent((prev) =>
              Math.max(prev, 5 + Math.floor((currentIndex / Math.max(1, chunkFiles.length)) * 70))
            );
            const uploadFile = chunkBuilder
              ? await chunkBuilder(currentIndex)
              : chunkFiles[currentIndex];
            const rowMeta = computeChunkRowMeta ? computeChunkRowMeta(currentIndex) : {};
            const singleLongRequest = chunkFiles.length === 1;
            if (singleLongRequest) {
              progressPulse = setInterval(() => {
                setImportProgressPercent((prev) => Math.min(88, prev + 1));
              }, 450);
            }
            const res = await chinaOrderImportService.importExcelFile(uploadFile, source, rowMeta);
            if (!res || res.status?.code !== 200) throw new Error(res?.error?.message || `Failed to import ${source} file.`);

            const s = res.data.summary || {};
            merged.orderDetails.total += s.orderDetails?.total || 0;
            merged.orderDetails.created += s.orderDetails?.created || 0;
            merged.orderDetails.updated += s.orderDetails?.updated || 0;
            merged.orderDetails.failedRows.push(...(s.orderDetails?.failedRows || []));
            merged.orderDetails.duplicateRows = mergeDuplicateRows(
              merged.orderDetails.duplicateRows,
              s.orderDetails?.duplicateRows || []
            );

            merged.postageToInd.total += s.postageToInd?.total || 0;
            merged.postageToInd.created += s.postageToInd?.created || 0;
            merged.postageToInd.updated += s.postageToInd?.updated || 0;
            merged.postageToInd.failedRows.push(...(s.postageToInd?.failedRows || []));
            merged.postageToInd.duplicateRows = mergeDuplicateRows(
              merged.postageToInd.duplicateRows,
              s.postageToInd?.duplicateRows || []
            );

            if (res?.data?.sheetSync) {
              pendingSheetSyncJobsRef.current = mergeSheetSyncJobsFromResponse(
                pendingSheetSyncJobsRef.current,
                res.data.sheetSync
              );
            }
          } catch (err) {
            const batchError = `${source} (batch ${currentIndex + 1}): ${err.message}`;
            errors.push(batchError);
            merged.importErrors.push(batchError);
          } finally {
            if (progressPulse) clearInterval(progressPulse);
            doneBatches += 1;
            const pct = Math.min(90, Math.round((doneBatches / totalBatches) * 90));
            setImportProgressPercent(pct);
            setImportProgressLabel(
              pct >= 90
                ? `${source}: 90% complete (finalizing...)`
                : `${source}: ${pct}% complete`
            );
          }
        }
      };

      const workerCount = Math.min(IMPORT_BATCH_CONCURRENCY, chunkFiles.length);
      await Promise.all(Array.from({ length: workerCount }, () => runBatchWorker()));

      merged.orderDetails.duplicateRows = mergeDuplicateRows(
        merged.orderDetails.duplicateRows,
        importMeta?.orderDetails?.duplicateRows || []
      );
      merged.postageToInd.duplicateRows = mergeDuplicateRows(
        merged.postageToInd.duplicateRows,
        importMeta?.postageToInd?.duplicateRows || []
      );

      // Adjust updated count to full-file unique-key ceiling (avoids over-count across API chunks).
      // If uniqueKeys is 0 (e.g. header match failed in buildImportChunks), do not cap — that would force Updated to 0.
      if (!ENABLE_DIRECT_IMPORT_UPLOAD) {
        const orderUk = Number(importMeta?.orderDetails?.uniqueKeys || 0);
        if (orderUk > 0) {
          const maxOrderUpdated = Math.max(0, orderUk - Number(merged.orderDetails.created || 0));
          merged.orderDetails.updated = Math.min(merged.orderDetails.updated, maxOrderUpdated);
        }
        const postageUk = Number(importMeta?.postageToInd?.uniqueKeys || 0);
        if (postageUk > 0) {
          const maxPostageUpdated = Math.max(0, postageUk - Number(merged.postageToInd.created || 0));
          merged.postageToInd.updated = Math.min(merged.postageToInd.updated, maxPostageUpdated);
        }
      }

      importResults.push({ source, summary: merged });
      toast.success(
        `${source}: ${merged.orderDetails.created} new + ${merged.orderDetails.updated} updated orders, ` +
        `${merged.postageToInd.created} new + ${merged.postageToInd.updated} updated postage records.`
      );
    }

    if (yekyFile)  { yekyFileRef.current.value  = ''; setYekyFileName(null);  }
    if (sunnyFile) { sunnyFileRef.current.value = ''; setSunnyFileName(null); }

    errors.forEach(e => toast.error(e));
    if (importResults.length > 0) {
      setFailedSummary(importResults);
      setShowFailedModal(true);
    }
    const sheetJobsAfterImport = pendingSheetSyncJobsRef.current;
    pendingSheetSyncJobsRef.current = [];
    if (sheetJobsAfterImport.length > 0) {
      beginSheetSyncProgressFromJobs(sheetJobsAfterImport);
    }
    setIsImporting(false);
    setImportProgressPercent(100);
    setImportProgressLabel("100% complete");
    setTimeout(() => setShowImportProgressModal(false), 450);
    fetchRecords(1, search, pagination.limit);
  };

  // ---- Edit record ----
  const handleEditSave = async (formData) => {
    setActionLoading(true);
    try {
      const res = await chinaOrderImportService.updateOrderDetailsRecord(editRow._id, formData);
      if (res?.status?.code === 200) {
        setRecords(prev => prev.map(r => r._id === editRow._id ? { ...r, ...res.data.record } : r));
        toast.success('Record updated successfully.');
        setEditRow(null);
        if (res.data?.sheetSync?.job) setSheetSyncJob(res.data.sheetSync.job);
      } else {
        toast.error(res?.error?.message || 'Failed to update record.');
      }
    } catch { toast.error('Failed to update record.'); }
    finally { setActionLoading(false); }
  };

  // ---- Delete record ----
  const handleDeleteConfirm = async (password) => {
    setActionLoading(true);
    try {
      const res = await chinaOrderImportService.deleteOrderDetailsRecord(deleteRow._id, password);
      if (res?.status?.code === 200) {
        setRecords(prev => prev.filter(r => r._id !== deleteRow._id));
        setPagination(prev => ({ ...prev, total: prev.total - 1 }));
        toast.success('Record deleted successfully.');
        setDeleteRow(null);
        if (res.data?.sheetSync?.job) setSheetSyncJob(res.data.sheetSync.job);
      } else {
        toast.error(res?.error?.message || 'Failed to delete record.');
      }
    } catch { toast.error('Failed to delete record.'); }
    finally { setActionLoading(false); }
  };

  const handleDeleteAllConfirm = async (password) => {
    setActionLoading(true);
    try {
      const res = await chinaOrderImportService.deleteAllOrderDetails(password);
      if (res?.status?.code === 200) {
        const n = res.data?.deletedCount ?? 0;
        toast.success(res.data?.message || `Deleted ${n} record(s).`);
        setRecords([]);
        setPagination((prev) => ({ ...prev, total: 0, totalPages: 1, page: 1 }));
        setShowDeleteAllModal(false);
        if (res.data?.sheetSync?.job) setSheetSyncJob(res.data.sheetSync.job);
      } else {
        toast.error(res?.error?.message || 'Failed to delete all records.');
      }
    } catch {
      toast.error('Failed to delete all records.');
    } finally {
      setActionLoading(false);
    }
  };

  const EXPORT_PAGE_SIZE = 5000;
  const FETCH_PHASE_MAX_PCT = 88;

  // ---- Export — paged GET (progress per backend response), then build xlsx ----
  const handleExport = async () => {
    setIsExporting(true);
    setShowExportProgressModal(true);
    setExportProgressPercent(2);
    setExportProgressLabel('Starting export…');
    try {
      const { rows, total } = await fetchAllRowsPaged({
        fetchPage: (params) => chinaOrderImportService.getOrderDetails(params),
        pageSize: EXPORT_PAGE_SIZE,
        extraQuery: {
          search,
          startDate,
          endDate,
          indiaSNoType,
          indiaSNoCustomPrefix: indiaSNoType === 'custom' ? indiaSNoCustomPrefix : '',
          source: filterSource === 'custom' ? String(filterSourceCustom || '').trim() : filterSource,
          status: filterStatus === 'custom' ? String(filterStatusCustom || '').trim() : filterStatus,
        },
        onProgress: ({ loaded, total: t, page, totalPages }) => {
          const denom = Math.max(1, t);
          const pct = Math.min(
            FETCH_PHASE_MAX_PCT,
            Math.max(5, Math.round((loaded / denom) * FETCH_PHASE_MAX_PCT))
          );
          setExportProgressPercent(pct);
          setExportProgressLabel(
            `Loaded ${loaded} of ${t} row(s) — page ${page} / ${totalPages}`
          );
        },
      });

      if (total === 0 || rows.length === 0) {
        toast.warn('No data to export.');
        setShowExportProgressModal(false);
        return;
      }

      setExportProgressLabel('Building Excel file…');
      setExportProgressPercent(92);

      const EXPORT_COLS = [
        { header: 'Source', key: 'source' },
        ...TABLE_COLUMNS.map(c => ({ header: c.label, key: c.key })),
      ];

      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet('Order Details');
      sheet.addRow(EXPORT_COLS.map(c => c.header));
      sheet.getRow(1).font = { bold: true };
      rows.forEach((row) => {
        sheet.addRow(EXPORT_COLS.map((c) => row[c.key] ?? ''));
      });

      EXPORT_COLS.forEach((col, idx) => {
        const colNum = idx + 1;
        let maxLen = col.header.length;
        sheet.getColumn(colNum).eachCell({ includeEmpty: false }, (cell) => {
          const len = cell.value ? String(cell.value).length : 0;
          if (len > maxLen) maxLen = len;
        });
        sheet.getColumn(colNum).width = Math.min(Math.max(maxLen + 2, 10), 50);
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'OrderDetails_Export.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);

      setExportProgressPercent(100);
      setExportProgressLabel('Export complete');
      toast.success(`Exported ${rows.length} record(s).`);
      setTimeout(() => setShowExportProgressModal(false), 500);
    } catch (e) {
      toast.error(e?.message || 'Export failed.');
      setShowExportProgressModal(false);
    } finally {
      setIsExporting(false);
    }
  };

  const sheetJobsForBanner =
    sheetSyncTrackedJobs.length > 0
      ? sheetSyncTrackedJobs
      : sheetSyncJob
        ? [sheetSyncJob]
        : [];

  const sheetTotalRows = sheetJobsForBanner.reduce((s, j) => s + (Number(j.totalRows) || 0), 0);
  const sheetSyncedRows = sheetJobsForBanner.reduce((s, j) => s + (Number(j.syncedRows) || 0), 0);
  const sheetSkippedTotal = sheetJobsForBanner.reduce((s, j) => s + (Number(j.skippedRows) || 0), 0);
  const sheetTargetRows = Math.max(0, sheetTotalRows - sheetSkippedTotal);
  const sheetProgressPct =
    sheetTargetRows > 0
      ? Math.min(100, Math.round((100 * sheetSyncedRows) / sheetTargetRows))
      : sheetTotalRows > 0
        ? Math.min(100, Math.round((100 * sheetSyncedRows) / sheetTotalRows))
        : 0;
  const sheetFailedTotal = sheetJobsForBanner.reduce((s, j) => s + (Number(j.failedRows) || 0), 0);
  const bannerStatusWord = sheetSyncAggregateStatusWord(sheetJobsForBanner);
  const sheetWarnBanner = sheetJobsForBanner.find((j) => j.sheetSizeWarning)?.sheetSizeWarning;
  const sheetErrBanner = sheetJobsForBanner.find((j) => j.lastError && j.status !== 'cancelled')?.lastError;
  const sheetSyncCanCancel = sheetJobsForBanner.some((j) => RUNNING_SHEET_SYNC.has(j.status));

  return (
    <div className="od-container">
      <div className="od-header">
        <h1>Order Details</h1>
      </div>

      {showFullSheetSyncConfirm && (
        <div
          className="od-modal-overlay od-full-sheet-sync-confirm-overlay"
          role="dialog"
          aria-labelledby="od-full-sheet-sync-title"
          aria-modal="true"
          onClick={() => !isFullSheetSyncing && setShowFullSheetSyncConfirm(false)}
        >
          <div className="od-modal" onClick={(e) => e.stopPropagation()}>
            <div className="od-modal-header">
              <h2 id="od-full-sheet-sync-title">Sync to live Google Sheet?</h2>
              <button
                type="button"
                className="od-modal-close"
                disabled={isFullSheetSyncing}
                onClick={() => setShowFullSheetSyncConfirm(false)}
                aria-label="Close"
              >
                &times;
              </button>
            </div>
            <div className="od-modal-body">
              <p className="od-modal-desc">
                 Sync <strong>All</strong> order details with Google Sheet. This may take some time for large data, but it will run in the
                  background so you can continue using the system.
              </p>
            </div>
            <div className="od-modal-footer od-full-sheet-sync-footer">
              <button
                type="button"
                className="od-btn-cancel"
                disabled={isFullSheetSyncing}
                onClick={() => setShowFullSheetSyncConfirm(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="cu-btn cu-btn-search od-full-sheet-sync-confirm-primary"
                disabled={isFullSheetSyncing}
                onClick={handleFullSheetSyncConfirmed}
              >
                {isFullSheetSyncing ? 'Queueing…' : 'Yes, sync all'}
              </button>
            </div>
          </div>
        </div>
      )}

      {sheetJobsForBanner.length > 0 && (
        <div className="od-sheet-sync-banner" role="status" aria-live="polite">
          <div className="od-sheet-sync-body">
            <strong>Google Sheet sync</strong>
            <span className="od-sheet-sync-meta">
              {bannerStatusWord}
              {' · '}
              {sheetSyncedRows.toLocaleString()} / {sheetTotalRows.toLocaleString()} rows synced
              {sheetSkippedTotal > 0
                ? ` · ${sheetSkippedTotal.toLocaleString()} skipped (not in DB)`
                : ''}
              {sheetFailedTotal > 0 ? ` · ${sheetFailedTotal.toLocaleString()} row(s) failed` : ''}
            </span>
            <div className="od-sheet-sync-progress-row">
              <div className="od-sheet-sync-track" aria-hidden="true">
                <div className="od-sheet-sync-fill" style={{ width: `${sheetProgressPct}%` }} />
              </div>
              <span className="od-sheet-sync-pct">{sheetProgressPct}%</span>
            </div>
            {sheetWarnBanner ? (
              <span className="od-sheet-sync-warn">{sheetWarnBanner}</span>
            ) : null}
            {sheetErrBanner ? (
              <span className="od-sheet-sync-err">{sheetErrBanner}</span>
            ) : null}
          </div>
          <div className="od-sheet-sync-actions">
            {sheetSyncCanCancel ? (
              <button
                type="button"
                className="od-sheet-sync-cancel"
                disabled={isCancellingSheetSync}
                onClick={handleCancelSheetSync}
              >
                {isCancellingSheetSync ? 'Cancelling…' : 'Cancel sync'}
              </button>
            ) : null}
            <button
              type="button"
              className="od-sheet-sync-dismiss"
              onClick={() => {
                setSheetSyncJob(null);
                setSheetSyncTrackedJobs([]);
                sheetSyncTrackedJobsRef.current = [];
                sheetSyncPollIdsRef.current = [];
              }}
              aria-label="Dismiss sheet sync status"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* ---- Upload Bar (horizontal) ---- */}
      <div className="od-import-bar">
        <div
          className={`od-upload-zone ${yekyFileName ? 'has-file' : ''} ${yekyDragOver ? 'od-upload-zone--drag' : ''}`}
          role="button"
          tabIndex={0}
          onClick={() => yekyFileRef.current?.click()}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); yekyFileRef.current?.click(); } }}
          onDragEnter={(e) => {
            e.preventDefault();
            e.stopPropagation();
            yekyDragDepth.current += 1;
            setYekyDragOver(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            e.stopPropagation();
            yekyDragDepth.current -= 1;
            if (yekyDragDepth.current <= 0) {
              yekyDragDepth.current = 0;
              setYekyDragOver(false);
            }
          }}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
            e.dataTransfer.dropEffect = 'copy';
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            yekyDragDepth.current = 0;
            setYekyDragOver(false);
            const file = firstExcelFromFileList(e.dataTransfer.files);
            if (file) assignExcelToInput(yekyFileRef, file, setYekyFileName);
            else if (e.dataTransfer.files?.length) toast.warn('Only .xlsx or .xls files are allowed.');
          }}
        >
          <input type="file" ref={yekyFileRef} accept=".xlsx,.xls" onChange={handleYekyFileChange} className="od-upload-input-screen-reader" aria-label="Yeky Excel file" />
          <span className="od-dot od-yeky-dot"></span>
          <span className="od-upload-label">YEKY DATA</span>
          <UploadIcon />
          <span className="od-upload-name">{yekyFileName || 'Browse or drag file'}</span>
          {yekyFileName ? (
            <button
              type="button"
              className="od-upload-clear"
              aria-label="Remove file"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={clearYekyFile}
            >
              ×
            </button>
          ) : (
            <span className="od-upload-hint">.xlsx / .xls</span>
          )}
        </div>

        <div
          className={`od-upload-zone ${sunnyFileName ? 'has-file' : ''} ${sunnyDragOver ? 'od-upload-zone--drag' : ''}`}
          role="button"
          tabIndex={0}
          onClick={() => sunnyFileRef.current?.click()}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); sunnyFileRef.current?.click(); } }}
          onDragEnter={(e) => {
            e.preventDefault();
            e.stopPropagation();
            sunnyDragDepth.current += 1;
            setSunnyDragOver(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            e.stopPropagation();
            sunnyDragDepth.current -= 1;
            if (sunnyDragDepth.current <= 0) {
              sunnyDragDepth.current = 0;
              setSunnyDragOver(false);
            }
          }}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
            e.dataTransfer.dropEffect = 'copy';
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            sunnyDragDepth.current = 0;
            setSunnyDragOver(false);
            const file = firstExcelFromFileList(e.dataTransfer.files);
            if (file) assignExcelToInput(sunnyFileRef, file, setSunnyFileName);
            else if (e.dataTransfer.files?.length) toast.warn('Only .xlsx or .xls files are allowed.');
          }}
        >
          <input type="file" ref={sunnyFileRef} accept=".xlsx,.xls" onChange={handleSunnyFileChange} className="od-upload-input-screen-reader" aria-label="Sunny Excel file" />
          <span className="od-dot od-sunny-dot"></span>
          <span className="od-upload-label">SUNNY DATA</span>
          <UploadIcon />
          <span className="od-upload-name">{sunnyFileName || 'Browse or drag file'}</span>
          {sunnyFileName ? (
            <button
              type="button"
              className="od-upload-clear"
              aria-label="Remove file"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={clearSunnyFile}
            >
              ×
            </button>
          ) : (
            <span className="od-upload-hint">.xlsx / .xls</span>
          )}
        </div>

        <div className="od-import-actions">
          <button
            type="button"
            className="od-btn-import"
            onClick={handleImportAll}
            disabled={isImporting || isFullSheetSyncing}
          >
            {isImporting ? 'Processing…' : <><UploadIcon /> Process &amp; Import</>}
          </button>
          <button
            type="button"
            className="od-btn-sheet-sync"
            onClick={() => setShowFullSheetSyncConfirm(true)}
            disabled={isImporting || isFullSheetSyncing}
            title="Queue sync of all Order Details to the live Google Sheet"
          >
            Sync to Google Sheet
          </button>
        </div>
      </div>

      {/* ---- Toolbar ---- */}
      <div className="cu-toolbar cu-toolbar-wrap cu-toolbar-compact">
        <form className="cu-search-bar" onSubmit={handleSearch}>
          <input
            type="text"
            className="cu-search-input"
            placeholder="Search by China S.No., India S.No., Order ID, Mask ID..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          {searchInput && (
            <button type="button" className="cu-clear-btn" onClick={handleClearSearch}>&times;</button>
          )}
          <button type="submit" className="cu-btn cu-btn-search">Search</button>
        </form>

        <div className="cu-date-filter">
          <select
            className="cu-select"
            value={indiaSNoType}
            onChange={(e) => {
              const next = e.target.value;
              setIndiaSNoType(next);
              if (next !== 'custom') {
                setIndiaSNoCustomPrefix('');
                fetchRecords(1, search, pagination.limit, startDate, endDate, next, '', filterSource, filterStatus, filterSourceCustom, filterStatusCustom);
              }
            }}
          >
            {INDIA_SNO_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value || 'all-india-sno'} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          {indiaSNoType === 'custom' && (
            <input
              type="text"
              className="cu-date-input"
              placeholder="Prefix"
              value={indiaSNoCustomPrefix}
              onChange={(e) => setIndiaSNoCustomPrefix(e.target.value)}
            />
          )}
        </div>

        {/* Source filter */}
        <div className="cu-date-filter">
          <select
            className="cu-select"
            value={filterSource}
            onChange={(e) => {
              const next = e.target.value;
              setFilterSource(next);
              if (next !== 'custom') {
                setFilterSourceCustom('');
                fetchRecords(1, search, pagination.limit, startDate, endDate, indiaSNoType, indiaSNoCustomPrefix, next, filterStatus, '', filterStatusCustom);
              }
            }}
          >
            {SOURCE_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value || 'all-source'} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          {filterSource === 'custom' && (
            <input
              type="text"
              className="cu-date-input"
              placeholder="Source"
              value={filterSourceCustom}
              onChange={(e) => setFilterSourceCustom(e.target.value)}
            />
          )}
        </div>

        {/* Status filter */}
        <div className="cu-date-filter">
          <select
            className="cu-select"
            value={filterStatus}
            onChange={(e) => {
              const next = e.target.value;
              setFilterStatus(next);
              if (next !== 'custom') {
                setFilterStatusCustom('');
                fetchRecords(1, search, pagination.limit, startDate, endDate, indiaSNoType, indiaSNoCustomPrefix, filterSource, next, filterSourceCustom, '');
              }
            }}
          >
            {STATUS_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value || 'all-status'} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          {filterStatus === 'custom' && (
            <input
              type="text"
              className="cu-date-input"
              placeholder="Status"
              value={filterStatusCustom}
              onChange={(e) => setFilterStatusCustom(e.target.value)}
            />
          )}
        </div>

        {/* Date range filter — Entry Date */}
        <div className="cu-date-filter">
          <label className="cu-label">Entry Date</label>
          <input
            type="date"
            className="cu-date-input"
            value={startDate}
            onChange={(e) => {
              const next = e.target.value;
              setStartDate(next);
              fetchRecords(1, search, pagination.limit, next, endDate, indiaSNoType, indiaSNoCustomPrefix, filterSource, filterStatus, filterSourceCustom, filterStatusCustom);
            }}
          />
          <label className="cu-label">To</label>
          <input
            type="date"
            className="cu-date-input"
            value={endDate}
            onChange={(e) => {
              const next = e.target.value;
              setEndDate(next);
              fetchRecords(1, search, pagination.limit, startDate, next, indiaSNoType, indiaSNoCustomPrefix, filterSource, filterStatus, filterSourceCustom, filterStatusCustom);
            }}
          />
          {hasCustomFilter && (
            <button type="button" className="cu-btn cu-btn-search cu-btn-apply" onClick={handleDateFilter} style={{borderRadius:'var(--cu-radius)'}}>Apply</button>
          )}
          {hasAnyActiveFilter && (
            <button type="button" className="cu-btn cu-btn-refresh" onClick={handleClearDates}>✕ Clear</button>
          )}
        </div>

        <div className="cu-toolbar-right">
          <label className="cu-label">Show</label>
          <select className="cu-select" value={pagination.limit} onChange={handleLimitChange}>
            {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          <label className="cu-label">per page</label>
          <span className="cu-total-badge">{pagination.total} total</span>
          <button
            type="button"
            className="cu-btn cu-btn-refresh"
            onClick={() =>
              fetchRecords(
                pagination.page,
                search,
                pagination.limit,
                startDate,
                endDate,
                indiaSNoType,
                indiaSNoCustomPrefix,
                filterSource,
                filterStatus
              )
            }
          >
            ↻
          </button>
          <button type="button" className="cu-btn cu-btn-export" onClick={handleExport} disabled={isExporting}>
            {isExporting ? 'Exporting…' : 'Export'}
          </button>
          <button
            type="button"
            className="cu-btn cu-btn-delete-all"
            disabled={isLoading || pagination.total < 1}
            onClick={() => {
              setDeleteRow(null);
              setShowDeleteAllModal(true);
            }}
          >
            Delete all
          </button>
        </div>
      </div>

      {/* ---- Data Table ---- */}
      <div className="od-table-wrapper">
        {isLoading ? (
          <div className="cu-loading">Loading...</div>
        ) : (
          <table className="od-table">
            <thead>
              <tr>
                <th rowSpan="2" className="od-group-hd od-src-hd">Source</th>
                {/* <th colSpan={ORDER_COLS.length}    className="od-group-hd od-order-hd">ORDER DETAILS</th>
                <th colSpan={PURCHASE_COLS.length} className="od-group-hd od-purchase-hd">PURCHASE INFORMATION</th>
                <th colSpan={SHIPPING_COLS.length} className="od-group-hd od-shipping-hd">SHIPPING DETAILS</th>
                <th colSpan={MANUAL_COLS.length}   className="od-group-hd od-doa-hd">MANUAL</th>
                <th rowSpan="2" className="od-group-hd od-action-hd">Action</th> */}
              </tr>
              <tr>
                {TABLE_COLUMNS.map((c) => (
                  <th
                    key={c.key}
                    className={c.wrap ? `od-sub-hd od-sub-hd-wrap od-sub-hd-wrap--${c.wrap}` : 'od-sub-hd'}
                  >
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {records.length > 0 ? (
                records.map((row, i) => (
                  <tr key={row._id || i} className="od-tr">
                    <td className="od-td"><span className={`od-badge ${(row.source || '').toLowerCase()}`}>{row.source || '-'}</span></td>
                    {TABLE_COLUMNS.map((c) => (
                      <td
                        key={c.key}
                        className={c.wrap ? `od-td od-td-wrap od-td-wrap--${c.wrap}` : 'od-td'}
                      >
                        {c.key === 'estimatedDOA' ? (
                          <input type="text" className="od-doa-input" value={row.estimatedDOA || ''} readOnly placeholder="-" />
                        ) : c.key === 'url' && row.url ? (
                          <a href={row.url} target="_blank" rel="noopener noreferrer" className="od-url-link" title={row.url}>{row.url}</a>
                        ) : (
                          row[c.key]?.toString() || ''
                        )}
                      </td>
                    ))}
                    <td className="od-td">
                      <div className="cu-action-cell">
                        <button className="cu-action-btn cu-action-btn-edit"   onClick={() => setEditRow(row)}>Edit</button>
                        <button className="cu-action-btn cu-action-btn-delete" onClick={() => setDeleteRow(row)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={TABLE_COLUMNS.length + 1} className="od-empty">
                    {search ? `No records found for "${search}".` : 'No data imported yet. Please select file(s) above and click "Process & Import".'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* ---- Pagination ---- */}
      {pagination.totalPages > 1 && (
        <div className="cu-pagination">
          <button className="cu-page-btn" disabled={pagination.page <= 1} onClick={() => handlePageChange(1)}>«</button>
          <button className="cu-page-btn" disabled={pagination.page <= 1} onClick={() => handlePageChange(pagination.page - 1)}>‹ Prev</button>
          <span className="cu-page-info">Page {pagination.page} of {pagination.totalPages}</span>
          <button className="cu-page-btn" disabled={pagination.page >= pagination.totalPages} onClick={() => handlePageChange(pagination.page + 1)}>Next ›</button>
          <button className="cu-page-btn" disabled={pagination.page >= pagination.totalPages} onClick={() => handlePageChange(pagination.totalPages)}>»</button>
        </div>
      )}

      {/* ---- Edit Modal ---- */}
      {editRow && (
        <EditModal
          title="Edit Order Details"
          fields={[
            { key: 'chinaSNo',        label: 'China S.No.',        readOnly: true },
            { key: 'indiaSNo',        label: 'India S. No.' },
            { key: 'entryDate',       label: 'Entry Date',         type: 'date' },
            { key: 'orderId',         label: 'Order ID' },
            { key: 'qty',             label: 'Qty' },
            { key: 'prodName',        label: 'Prod Name' },
            { key: 'orderComments',   label: 'Order Comments',     type: 'textarea' },
            { key: 'url',             label: 'URL' },
            { key: 'purchaseQty',     label: 'Purchase Qty.' },
            { key: 'unit',            label: 'Unit' },
            { key: 'variant',         label: 'Variant' },
            { key: 'unitPrice',       label: 'Unit Price' },
            { key: 'qtyPrice',        label: 'Qty Price' },
            { key: 'localShipping',   label: 'Local Shipping' },
            { key: 'totalPrice',      label: 'Total Price' },
            { key: 'maskId',          label: 'Mask ID' },
            { key: 'shippingDate',    label: 'Shipping Date',      type: 'date' },
            { key: 'shippingCompany', label: 'Shipping Comments' },
            { key: 'qtyShipped',      label: 'Qty Shipped' },
            { key: 'status',          label: 'Status' },
            { key: 'qtyReceived',     label: 'Qty Received',       type: 'number' },
            { key: 'productStatus',   label: 'Product Status' },
            { key: 'internalNotes',   label: 'Internal Notes',     type: 'textarea', fullWidth: true },
            { key: 'hsn',             label: 'HSN' },
            { key: 'estimatedDOA',    label: 'Estimated DOA',      readOnly: true },
            { key: 'source',          label: 'Source' },
          ]}
          initialData={editRow}
          onSave={handleEditSave}
          onClose={() => setEditRow(null)}
          loading={actionLoading}
        />
      )}

      {/* ---- Delete Modal ---- */}
      {deleteRow && (
        <DeleteModal
          title={`Delete — China S.No. ${deleteRow.chinaSNo}`}
          onConfirm={handleDeleteConfirm}
          onClose={() => setDeleteRow(null)}
          loading={actionLoading}
        />
      )}

      {showDeleteAllModal && (
        <DeleteModal
          title="Delete all Order Details"
          description={(
            <>
              Every row in <strong>Order Details</strong> will be removed from the database. This cannot be undone.
              <br />
              Enter your admin password to confirm.
            </>
          )}
          confirmButtonText="Delete all"
          onConfirm={handleDeleteAllConfirm}
          onClose={() => setShowDeleteAllModal(false)}
          loading={actionLoading}
        />
      )}

      {showFailedModal && (
        <div className="od-modal-overlay" onClick={() => setShowFailedModal(false)}>
          <div className="od-modal od-summary-modal" onClick={(e) => e.stopPropagation()}>
            <div className="od-modal-header">
              <h2>Import Summary</h2>
              <button className="od-modal-close" onClick={() => setShowFailedModal(false)}>&times;</button>
            </div>
            <div className="od-modal-body">
              {failedSummary.map((item) => {
                const odFailed = item.summary?.orderDetails?.failedRows || [];
                const piFailed = item.summary?.postageToInd?.failedRows || [];
                const odDuplicates = item.summary?.orderDetails?.duplicateRows || [];
                const piDuplicates = item.summary?.postageToInd?.duplicateRows || [];
                const odCreated = item.summary?.orderDetails?.created || 0;
                const odUpdated = item.summary?.orderDetails?.updated || 0;
                const piCreated = item.summary?.postageToInd?.created || 0;
                const piUpdated = item.summary?.postageToInd?.updated || 0;
                const importErrors = item.summary?.importErrors || [];
                const odIssueCount = odFailed.length + odDuplicates.length;
                const piIssueCount = piFailed.length + piDuplicates.length;
                return (
                  <div key={item.source} className="od-summary-source-card">
                    <p className="od-modal-desc"><strong>Source: {item.source}</strong></p>
                    {importErrors.length > 0 && (
                      <div className="od-summary-list" style={{ marginBottom: '10px', maxHeight: '110px' }}>
                        {importErrors.map((msg, idx) => (
                          <p key={`import-err-${idx}`} className="od-modal-desc od-failed-msg">
                            {renderImportApiError(msg)}
                          </p>
                        ))}
                      </div>
                    )}

                    <div className="od-summary-grid">
                      <div className="od-summary-section">
                        <div className="od-summary-title">Order Details</div>
                        <div className="od-summary-badges">
                          <span className="od-chip od-chip-created">Created: {odCreated}</span>
                          <span className="od-chip od-chip-updated">Updated: {odUpdated}</span>
                          <span className="od-chip od-chip-failed">Issues: {odIssueCount}</span>
                        </div>
                        <div className="od-summary-list">
                          {odFailed.length === 0 && odDuplicates.length === 0 ? (
                            <p className="od-modal-desc">No failed rows</p>
                          ) : (
                            <>
                              {odFailed.map((f, idx) => (
                                <p key={`od-${idx}`} className="od-modal-desc od-failed-msg">
                                  <span className="od-sheet-value">{f.identifier}</span>
                                  <span className="od-failed-sep"> — </span>
                                  <span className="od-failed-reason">{renderImportReason(f.reason)}</span>
                                </p>
                              ))}
                              {odDuplicates.length > 0 && (
                                <p className="od-dedupe-note">
                                  Same key repeated in the sheet: one record is still saved — we use the
                                  {' '}<strong>last</strong> row for that key (insert or update completed).
                                </p>
                              )}
                              {odDuplicates.map((d, idx) => (
                                <p key={`od-dup-${idx}`} className="od-modal-desc od-failed-msg">
                                  <span className="od-failed-muted">China S.No.</span>{' '}
                                  <span className="od-sheet-value">{d.key}</span>
                                  <span className="od-failed-muted"> found </span>
                                  <span className="od-sheet-value">{d.count}</span>
                                  <span className="od-failed-muted"> times in file</span>
                                </p>
                              ))}
                            </>
                          )}
                        </div>
                      </div>

                      <div className="od-summary-section">
                        <div className="od-summary-title">Postage to India</div>
                        <div className="od-summary-badges">
                          <span className="od-chip od-chip-created">Created: {piCreated}</span>
                          <span className="od-chip od-chip-updated">Updated: {piUpdated}</span>
                          <span className="od-chip od-chip-failed">Issues: {piIssueCount}</span>
                        </div>
                        <div className="od-summary-list">
                          {piFailed.length === 0 && piDuplicates.length === 0 ? (
                            <p className="od-modal-desc">No failed rows</p>
                          ) : (
                            <>
                              {piFailed.map((f, idx) => (
                                <p key={`pi-${idx}`} className="od-modal-desc od-failed-msg">
                                  <span className="od-sheet-value">{f.identifier}</span>
                                  <span className="od-failed-sep"> — </span>
                                  <span className="od-failed-reason">{renderImportReason(f.reason)}</span>
                                </p>
                              ))}
                              {piDuplicates.length > 0 && (
                                <p className="od-dedupe-note">
                                  Same invoice number repeated: one record is still saved — we use the
                                  {' '}<strong>last</strong> row for that invoice (insert or update completed).
                                </p>
                              )}
                              {piDuplicates.map((d, idx) => (
                                <p key={`pi-dup-${idx}`} className="od-modal-desc od-failed-msg">
                                  <span className="od-sheet-field">Invoice No</span>{' '}
                                  <span className="od-sheet-value">{d.key}</span>
                                  <span className="od-failed-muted"> found </span>
                                  <span className="od-sheet-value">{d.count}</span>
                                  <span className="od-failed-muted"> times in file</span>
                                </p>
                              ))}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="od-modal-footer">
              <button className="cu-btn cu-btn-search" onClick={() => setShowFailedModal(false)}>OK</button>
            </div>
          </div>
        </div>
      )}

      {showImportProgressModal && (
        <div className="od-modal-overlay">
          <div className="od-modal od-progress-modal" onClick={(e) => e.stopPropagation()}>
            <div className="od-modal-header">
              <h2>Import Progress</h2>
            </div>
            <div className="od-modal-body">
              <p className="od-modal-desc" style={{ marginBottom: '8px' }}>{importProgressLabel}</p>
              <div className="od-progress-track">
                <div className="od-progress-fill" style={{ width: `${importProgressPercent}%` }} />
              </div>
              <div className="od-progress-pct">{importProgressPercent}%</div>
            </div>
          </div>
        </div>
      )}

      {showExportProgressModal && (
        <div className="cu-export-progress-overlay">
          <div className="cu-export-progress-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cu-export-progress-header">
              <h2>Export Progress</h2>
            </div>
            <div className="cu-export-progress-body">
              <p className="cu-export-progress-label">{exportProgressLabel}</p>
              <div className="cu-export-progress-track">
                <div className="cu-export-progress-fill" style={{ width: `${exportProgressPercent}%` }} />
              </div>
              <div className="cu-export-progress-pct">{exportProgressPercent}%</div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
